import { useFetchZipCodes } from "@/queries/useFetchZipCodes.ts";
import { Autocomplete, CircularProgress, TextField, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

type Option = {
    id: string;
    name: string;
    displayName: string;
};

const createOption = (zip: string, city?: string | null, state?: string | null): Option => ({
    id: zip,
    name: zip,
    displayName: city && state ? `${zip} ${city} ${state}` : zip,
});

const ZipCodeAutocomplete = () => {
    const form = useFormContext();
    const formValue = form.watch("zip");

    const [inputValue, setInputValue] = useState("");
    const [searchString, setSearchString] = useState("");

    // Initialize selectedZipCodeOption from formValue using lazy initializer
    const [selectedZipCodeOption, setSelectedZipCodeOption] = useState<Option | undefined>(() =>
        formValue ? createOption(formValue) : undefined,
    );

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchZipCodes({
        zipCode: searchString,
        isQueryEnabled: searchString.length >= 2,
    });

    // Convert API data to options
    const options: Option[] = useMemo(() => {
        if (!data) {
            return [];
        }

        return data.map((zipCodeDocument) =>
            createOption(
                zipCodeDocument.zIPPostal,
                zipCodeDocument.city,
                zipCodeDocument.stateProvince,
            ),
        );
    }, [data]);

    // Trigger search when input value changes
    useEffect(() => {
        if (inputValue.length >= 2) {
            setSearchString(inputValue);
        }
    }, [inputValue]);

    // Update city and state when a zip code option is selected
    useEffect(() => {
        if (!(selectedZipCodeOption && data)) {
            return;
        }

        const foundZipCode = data.find((zipCode) => zipCode.zIPPostal === selectedZipCodeOption.id);

        if (foundZipCode) {
            form.setValue("city", foundZipCode.city);
            form.setValue("state", foundZipCode.stateProvince);
            form.setValue("zip", foundZipCode.zIPPostal);
        }
    }, [selectedZipCodeOption, data]);

    return (
        <>
            {(isLoading || isRefetching) && <CircularProgress />}
            {isError && <Typography variant="h5">Error loading zip codes</Typography>}
            <Autocomplete
                value={selectedZipCodeOption ?? null}
                inputValue={inputValue}
                onInputChange={(_event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Enter a Zip Code"
                        size="small"
                        sx={{
                            "& .MuiAutocomplete-inputRoot": {
                                padding: "4px!important",
                            },
                            "& .MuiAutocomplete-input": {
                                paddingTop: "4px!important",
                                paddingBottom: "4px!important",
                            },
                            "& .MuiFormLabel-root": {
                                paddingTop: "8px!important",
                                paddingBottom: "8px!important",
                            },
                        }}
                    />
                )}
                options={options}
                loading={isLoading || isRefetching}
                getOptionLabel={(option: Option) => option.name}
                onChange={(_event, value) => {
                    setSelectedZipCodeOption(value ?? undefined);
                    if (!value) {
                        // Clear related fields when zip is cleared
                        form.setValue("city", "");
                        form.setValue("state", "");
                        form.setValue("zip", "");
                    }
                }}
                renderOption={(props, option) => <li {...props}>{option.displayName}</li>}
                autoHighlight
                blurOnSelect
            />
        </>
    );
};

export default ZipCodeAutocomplete;
